import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Action, State, StateContext } from '@ngxs/store';
import { ResetForm } from '@ngxs/form-plugin';
import { tap, catchError } from 'rxjs/operators';
import { Navigate } from '@ngxs/router-plugin';

import { BcStateListContext, BcStateForm } from '@bayclubs/state';
import { ContextService, CategoryOptionsModel, CourtTimeSlotModel } from '@bayclubs/court-booking-employee-api';

import * as actions from './core.actions';
import { CoreStateModel, CORE_FEATURE_KEY } from './core.state.model';
import { ClubSelectFormModel } from '../models/club-select-form.model';

@Injectable({ providedIn: 'root' })
@State<CoreStateModel>({
    name: CORE_FEATURE_KEY,
    defaults: {
        clubs: BcStateListContext.Empty(),
        categories: BcStateListContext.Empty(),
        courtEventStatuses: BcStateListContext.Empty(),
        tennisCourtTypes: BcStateListContext.Empty(),
        categoriesOptions: BcStateListContext.Empty(),
        courtTimeSlots: BcStateListContext.Empty(),
        courtVisibilityOptions: BcStateListContext.Empty(),
        selectedClub: null,
        selectedClubForm: BcStateForm.Empty(),
        clubCourts: BcStateListContext.Empty()
    }
})
export class CoreState {
    constructor(private contextService: ContextService, private router: Router) {}

    @Action(actions.LoadContextAction)
    loadContext(ctx: StateContext<CoreStateModel>) {
        return this.contextService.api10ContextGet().pipe(
            tap(response => {
                ctx.dispatch(new actions.LoadContextSuccessAction(response));
            }),
            catchError(error => {
                ctx.patchState({
                    clubs: BcStateListContext.Error(),
                    categories: BcStateListContext.Error(),
                    courtEventStatuses: BcStateListContext.Error(),
                    tennisCourtTypes: BcStateListContext.Error(),
                    categoriesOptions: BcStateListContext.Error(),
                    courtTimeSlots: BcStateListContext.Error(),
                    courtVisibilityOptions: BcStateListContext.Error()
                });
                throw error;
            })
        );
    }

    @Action(actions.LoadContextSuccessAction)
    loadContextSuccess(ctx: StateContext<CoreStateModel>, action: actions.LoadContextSuccessAction) {
        ctx.patchState({
            clubs: BcStateListContext.Loaded(action.context.clubs),
            categories: BcStateListContext.Loaded(action.context.categories),
            courtEventStatuses: BcStateListContext.Loaded(action.context.courtEventStatuses),
            tennisCourtTypes: BcStateListContext.Loaded(action.context.tennisCourtTypes),
            categoriesOptions: BcStateListContext.Loaded(action.context.categoriesOptions),
            courtTimeSlots: BcStateListContext.Loaded(action.context.courtTimeSlots),
            courtVisibilityOptions: BcStateListContext.Loaded(action.context.courtVisibilityOptions)
        });
    }

    @Action(actions.SetUpClubContext)
    setUpClubContext(ctx: StateContext<CoreStateModel>, action: actions.SetUpClubContext) {
        const state = ctx.getState();

        const selectedClub = action.clubId != null ? state.clubs.items.find(x => x.id === action.clubId) : state.clubs.items[0];

        ctx.patchState({
            selectedClub
        });

        ctx.dispatch(new ResetForm({ path: 'cbCore.selectedClubForm', value: ClubSelectFormModel.Init(selectedClub) }));
        ctx.dispatch(new actions.LoadClubCourtsAction(selectedClub));
    }

    @Action(actions.SelectedClubChanged)
    selectedClubChanged(ctx: StateContext<CoreStateModel>, action: actions.SelectedClubChanged) {
        const state = ctx.getState();

        const nextClubId = action.clubId ?? state.selectedClubForm?.model?.clubId;

        if (state.selectedClub && nextClubId && state.selectedClub.id !== nextClubId) {
            const newUrl = this.router.url.replace(state.selectedClub.id, nextClubId);

            const selectedClub = state.clubs.items.find(x => x.id === nextClubId);
            ctx.patchState({
                selectedClub,
                clubCourts: BcStateListContext.Empty()
            });

            ctx.dispatch([
                new Navigate([newUrl]),
                new actions.SelectedClubChangedSuccess(selectedClub),
                new actions.LoadClubCourtsAction(selectedClub)
            ]);
        }
    }

    @Action(actions.LoadClubCourtsAction)
    loadClubCourts(ctx: StateContext<CoreStateModel>, action: actions.LoadClubCourtsAction) {
        ctx.patchState({ clubCourts: BcStateListContext.Loading() });
        return this.contextService.api10ContextClubIdCourtsGet({ clubId: action.club.id }).pipe(
            tap(value => {
                ctx.dispatch(new actions.LoadClubCourtsSuccessAction(value));
            }),
            catchError(err => {
                ctx.patchState({ clubCourts: BcStateListContext.Error() });
                throw err;
            })
        );
    }

    @Action(actions.LoadClubCourtsSuccessAction)
    loadClubCourtsSuccess(ctx: StateContext<CoreStateModel>, action: actions.LoadClubCourtsSuccessAction) {
        ctx.patchState({ clubCourts: BcStateListContext.Loaded(action.courts) });
    }
}
